
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SolicitacaoFormData } from '@/types/solicitacao';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useSLACalculation } from './useSLACalculation';
import { nowInBrazil } from '@/utils/timezone';

export const useSolicitacaoCreate = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { calculateAndSetSLADeadlines } = useSLACalculation();

  const createSolicitacao = useMutation({
    mutationFn: async (formData: SolicitacaoFormData) => {
      console.log('Creating solicitação:', formData);
      
      // Usar a mesma data de abertura para cálculos de SLA
      const dataAberturaBrasil = nowInBrazil();
      
      // Calcular automaticamente as datas limite de SLA
      const slaDeadlines = await calculateAndSetSLADeadlines(
        formData.categoria_id,
        formData.grupo_responsavel_id,
        dataAberturaBrasil
      );
      
      // Buscar client_id do solicitante se fornecido e não tiver client_id no formData
      // Isso garante que o ticket sempre tenha o client_id correto do solicitante
      let clientIdFinal = formData.client_id;
      
      if (formData.solicitante_id && !formData.client_id) {
        const { data: solicitanteData } = await supabase
          .from('users')
          .select('client_id')
          .eq('id', formData.solicitante_id)
          .single();
        
        if (solicitanteData?.client_id) {
          clientIdFinal = solicitanteData.client_id;
          console.log('Client_id obtido do solicitante:', clientIdFinal);
        }
      }

      // Defesa em profundidade: se a categoria pertence a um cliente
      // específico, alinhar o client_id final ao cliente da categoria.
      if (formData.categoria_id) {
        const { data: catData } = await supabase
          .from('categorias_servico')
          .select('client_id')
          .eq('id', formData.categoria_id)
          .single();
        if (catData?.client_id && catData.client_id !== clientIdFinal) {
          console.log('Ajustando client_id pelo cliente da categoria:', catData.client_id);
          clientIdFinal = catData.client_id;
        }
      }
      
      // Preparar dados para inserção, incluindo as datas limite calculadas
      const insertData = {
        titulo: formData.titulo,
        descricao: formData.descricao,
        tipo: formData.tipo,
        categoria_id: formData.categoria_id || null,
        sla_id: formData.sla_id || null,
        urgencia: formData.urgencia,
        impacto: formData.impacto,
        prioridade: formData.prioridade,
        status: formData.status,
        solicitante_id: formData.solicitante_id || null,
        client_id: clientIdFinal || null,
        grupo_responsavel_id: formData.grupo_responsavel_id || null,
        atendente_id: formData.atendente_id || null,
        canal_origem: formData.canal_origem,
        data_abertura: dataAberturaBrasil.toISOString(),
        data_limite_resposta: slaDeadlines.data_limite_resposta || formData.data_limite_resposta || null,
        data_limite_resolucao: slaDeadlines.data_limite_resolucao || formData.data_limite_resolucao || null,
        origem_id: formData.origem_id || null,
        ativos_envolvidos: formData.ativos_envolvidos || null,
        notas_internas: formData.notas_internas || null,
        tags: formData.tags || null,
        criado_por: user?.id || null,
      };

      console.log('Inserting data with data_abertura:', insertData.data_abertura);
      
      const { data, error } = await supabase
        .from('solicitacoes')
        .insert(insertData as any)
        .select()
        .single();

      if (error) {
        console.error('Error creating solicitação:', error);
        throw error;
      }

      // Forçar atualização da data_abertura se o banco sobrescreveu
      if (data && data.data_abertura !== insertData.data_abertura) {
        console.log('Database overrode data_abertura, forcing update...');
        const { error: updateError } = await supabase
          .from('solicitacoes')
          .update({ 
            data_abertura: insertData.data_abertura,
            data_limite_resposta: slaDeadlines.data_limite_resposta || formData.data_limite_resposta,
            data_limite_resolucao: slaDeadlines.data_limite_resolucao || formData.data_limite_resolucao
          })
          .eq('id', data.id);
          
        if (updateError) {
          console.error('Error updating data_abertura:', updateError);
        } else {
          console.log('Successfully updated data_abertura to Brazil timezone');
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['solicitacoes'] });
      toast({
        title: "Sucesso",
        description: "Solicitação criada com sucesso! Prazos de SLA calculados automaticamente.",
      });
    },
    onError: (error) => {
      console.error('Error creating solicitação:', error);
      toast({
        title: "Erro",
        description: "Erro ao criar solicitação. Tente novamente.",
        variant: "destructive",
      });
    },
  });

  return createSolicitacao;
};
